import type { Context } from "./cond";
import { Effect, RoundEffect, ConsumeEffect } from "./effect";
import type { BattleEvent } from "./events";
import { BattleStatus } from "./pet";
import type { Pet } from "./pet";
import { opposite, type Side } from "./operation";
import type { Seer } from "./seer";
import { Timing } from "./timing";
import { reconcileSoulmarkEffects } from "./soulmarkSync";

/**
 * EffectFilter 的数据结构或可选取值约定。
 */
type EffectFilter = (effect: Effect, definition: boolean) => boolean;

/** 准备仅建立独立运行状态，绝不执行声明中的业务效果。 */
export function initializeSoulmarks(left: Seer, right: Seer) {
    for (const [seer, side] of [
        [left, "left"],
        [right, "right"],
    ] as const)
        for (const pet of seer.petPackage) pet.initializeSoulmark(`soulmark:${side}:${pet.序号}`);
}

/** 阵容确定后的开局入口：全员初始化先于任一 battleBegin。 */
export function beginBattle(left: Seer, right: Seer, events: BattleEvent[] = []) {
    initializeSoulmarks(left, right);
    for (const timing of [Timing.battleBegin, Timing.petOn]) {
        for (const [seer, opponent, side] of [
            [left, right, "left"],
            [right, left, "right"],
        ] as const) {
            /**
             * target，供本模块相关规则使用。
             */
            const target = opponent.getBattlePet();
            /**
             * pets，供本模块相关规则使用。
             */
            const pets = timing === Timing.battleBegin ? seer.petPackage : [seer.getActivePet()];
            for (const pet of pets) {
                if (!pet || !target || pet.战斗状态 === BattleStatus.DEAD) continue;
                events.push({ timing, side, petId: pet.序号 });
                /**
                 * definitions，供本模块相关规则使用。
                 */
                const definitions =
                    pet === seer.getActivePet()
                        ? pet.技能组.flatMap((skill) => skill.getEffectsAt(timing))
                        : [];
                dispatchBattleTiming(
                    timing,
                    {
                        sbjPet: pet,
                        objPet: target,
                        sbjSeer: seer,
                        objSeer: opponent,
                        timing,
                        side,
                        events,
                    },
                    definitions,
                );
            }
        }
    }
}

/** 预先捕获事件监听者；命中阶段在注册后读取回合效果，分段命中门共用执行记录。 */
export function captureBattleTiming(timing: Timing, context: Context, definitions: Effect[] = []) {
    /**
     * { sbjPet, objPet, sbjSeer, objSeer }，供本模块相关规则使用。
     */
    const { sbjPet, objPet, sbjSeer, objSeer } = context;
    /**
     * side，供本模块相关规则使用。
     */
    const side = context.side ?? "left";
    /**
     * event，供本模块相关规则使用。
     */
    const event = {
        ...context,
        timing,
        eventPet: sbjPet,
        eventTarget: objPet,
        eventSeer: sbjSeer,
        eventSide: side,
    };
    /**
     * left，供本模块相关规则使用。
     */
    const left = side === "left" ? sbjSeer : objSeer;
    /**
     * right，供本模块相关规则使用。
     */
    const right = side === "left" ? objSeer : sbjSeer;
    initializeSoulmarks(left, right);
    reconcileSoulmarkEffects(context);
    /**
     * defenderContext，供本模块相关规则使用。
     */
    const defenderContext: Context = {
        ...event,
        sbjPet: objPet,
        objPet: sbjPet,
        sbjSeer: objSeer,
        objSeer: sbjSeer,
        side: opposite(side),
    };
    /**
     * attacker，供本模块相关规则使用。
     */
    const attacker = sbjPet.captureTiming(timing, event, definitions);
    /**
     * defender，供本模块相关规则使用。
     */
    const defender = objPet.captureTiming(timing, defenderContext, [], "opponent");
    /**
     * captures，供本模块相关规则使用。
     */
    const captures = new Map<Pet, ReturnType<Pet["captureSoulmarkTiming"]>>();
    /**
     * collect，供本模块相关规则使用。
     */
    const collect = (seer: Seer, opponent: Seer, ownerSide: Side) => {
        for (const pet of seer.petPackage) {
            if (
                pet.战斗状态 === BattleStatus.DEAD &&
                !(pet === sbjPet && timing === Timing.petDead)
            )
                continue;
            captures.set(
                pet,
                pet.captureSoulmarkTiming(timing, {
                    ...event,
                    sbjPet: pet,
                    sbjSeer: seer,
                    objSeer: opponent,
                    objPet: pet === sbjPet ? objPet : (opponent.getBattlePet() ?? objPet),
                    side: ownerSide,
                }),
            );
        }
    };
    collect(left, right, "left");
    collect(right, left, "right");
    // 独立 Move/Pet 测试也可使用未装入背包的领域精灵。
    if (!captures.has(sbjPet)) captures.set(sbjPet, sbjPet.captureSoulmarkTiming(timing, event));
    if (!captures.has(objPet) && objPet.战斗状态 !== BattleStatus.DEAD)
        captures.set(objPet, objPet.captureSoulmarkTiming(timing, defenderContext));
    /**
     * combine，供本模块相关规则使用。
     */
    const combine =
        (regular: (filter?: EffectFilter) => void, soulmark?: (filter?: EffectFilter) => void) =>
        (filter: EffectFilter = () => true) => {
            // 跨技能与魂印来源保持同一生命周期顺序，常驻免疫不会提前消耗次数免疫。
            /**
             * lanes，供本模块相关规则使用。
             */
            const lanes: EffectFilter[] = [
                ...(timing === Timing.onSkillHit
                    ? [
                        (effect: Effect, definition: boolean) => definition && effect instanceof RoundEffect,
                        (effect: Effect, definition: boolean) => definition && !(effect instanceof RoundEffect),
                    ]
                    : [(_effect: Effect, definition: boolean) => definition]),
                (effect, definition) => !definition && !(effect instanceof RoundEffect || effect instanceof ConsumeEffect),
                (effect, definition) => !definition && effect instanceof RoundEffect,
                (effect, definition) => !definition && effect instanceof ConsumeEffect,
            ];
            for (const lane of lanes) {
                /**
                 * selected，供本模块相关规则使用。
                 */
                const selected: EffectFilter = (effect, definition) =>
                    lane(effect, definition) && filter(effect, definition);
                regular(selected);
                soulmark?.(selected);
            }
        };
    return {
        attacker: combine(attacker, captures.get(sbjPet)),
        defender: combine(defender, captures.get(objPet)),
        observers: (filter?: EffectFilter, includeDefender = false) => {
            for (const [pet, run] of captures)
                if (pet !== sbjPet && (includeDefender || pet !== objPet)) run(filter);
        },
    };
}

/** 公共事件保留原有单方技能派发，并向其余魂印监听者广播一次。 */
export function dispatchBattleTiming(timing: Timing, context: Context, definitions: Effect[] = []) {
    /**
     * hooks，供本模块相关规则使用。
     */
    const hooks = captureBattleTiming(timing, context, definitions);
    hooks.attacker();
    hooks.observers(undefined, true);
    reconcileSoulmarkEffects(context);
}
